import React, { FC, useCallback, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { FieldError, Group, Input, ToggleableField } from '../../components';
import { BackButton } from '../components/BackButton';
import { CenteredContent } from '../components/CenteredContent';
import { NextButton } from '../components/NextButton';
import { extractWebsiteFromEmail } from '../../util/format.util';
import {
  AuthStore,
  ProgressStore,
  RegistrationStore,
  RoutesStore,
  updateRegistration,
} from '../../context';
import { Registration } from '@stax-core/types-omni';
import { useToaster, useWriteableFieldFormValidation } from '../../hooks';
import { isValidUrl } from '../../util/validator.util';
import { coreapi } from '../../api';
import { MainForm } from '../components/application/MainForm';
import { history } from '../../history';

export const CustomIcon = styled.i`
  color: ${(props: { type?: string }) => {
    switch (props.type) {
      case 'success':
        return '#28CB35';
      case 'error':
        return '#be1e2d';
      default:
        return '#BDC9CC';
    }
  }};
`;

interface FieldValues {
  business_website: string | null;
}

export const WebsiteDetail: FC = () => {
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const { toaster, toast } = useToaster();
  const {
    state: { auth },
  } = useContext(AuthStore);
  const authToken = auth?.token ?? null;
  const { nextRoute, previousRoute } = useContext(RoutesStore);
  const {
    state: { lastVisitedRoute },
  } = useContext(ProgressStore);

  // This code is checking if the selling_frequency from registration.meta is equal to "never".
  // If it is, then it checks if the user was last on the previous route and redirects to the next route or vice versa.
  // This is done to allow skipping of the website page when the selling frequency is set to "never".
  useEffect(() => {
    if (registration?.meta?.selling_frequency === 'never') {
      if (lastVisitedRoute === previousRoute?.path) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        history.push(nextRoute?.path!);
      } else if (lastVisitedRoute === nextRoute?.path) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        history.push(previousRoute?.path!);
      }
    }
  }, [
    lastVisitedRoute,
    nextRoute?.path,
    previousRoute?.path,
    registration?.meta?.selling_frequency,
  ]);

  const validate = useWriteableFieldFormValidation((values: FieldValues) => {
    const errors: Partial<Record<keyof FieldValues, any>> = {};
    if (
      !values.business_website ||
      !isValidUrl(values.business_website, false)
    ) {
      errors.business_website = 'Please enter a valid URL/website address';
    }
    return errors;
  });

  const onSubmit = useCallback(
    async (formValues: FieldValues) => {
      try {
        const r = await coreapi.put(authToken!, '/team/registration', {
          business_website: formValues.business_website,
        });
        dispatch(updateRegistration(r));
        return true;
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toast, toaster],
  );

  const getWebsite = useCallback(() => {
    return (
      registration?.business_website ||
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      extractWebsiteFromEmail(registration?.business_email || auth?.user?.email!)
    );
  }, [
    auth?.user?.email,
    registration?.business_email,
    registration?.business_website,
  ]);

  const getCustomIconProps = formProps => {
    const hasError = formProps.errors.business_website;
    const hasValue = formProps.values.business_website?.length;

    return {
      className: hasError && hasValue
        ? 'fas fa-times-circle'
        : hasValue
        ? 'fas fa-check-circle'
        : 'fa fa-minus-circle',
      type: hasError && hasValue ? 'error' : hasValue ? 'success' : undefined,
    };
  };

  return (
    <MainForm<FieldValues>
      initialValues={mapInitialFormValues(registration)}
      validate={validate}
      onSubmit={onSubmit}
    >
      {formProps => (
        <CenteredContent
          id="website"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <BackButton id="website-back" />

          <h2>Website</h2>
          <p>How do people find you online?</p>
          <div>
            <Group direction="column">
              <ToggleableField
                name="business_website"
                label="URL"
                toggleable={false}
                defaultMode="edit"
                aria-describedby={'URL'}
                initialValue={getWebsite()!}
              >
                {props => (
                  <Input
                    {...props.input}
                    customIcon={<CustomIcon {...getCustomIconProps(formProps)} />}
                    style={{ paddingRight: '40px' }}
                  />
                )}
              </ToggleableField>
              <FieldError name="business_website" />
            </Group>
          </div>
          <NextButton id="website-continue" formProps={formProps} />
        </CenteredContent>
      )}
    </MainForm>
  );
};

function mapInitialFormValues(
  registration: Registration | null,
): Partial<FieldValues> {
  return registration
    ? {
        business_website: registration.business_website || null,
      }
    : ({
        business_website: null,
      } as FieldValues);
}
